import React, { useEffect, useState, useRef } from "react";
import { Box, CircularProgress, Alert } from "@mui/material";
import { useSupabase } from "../providers/SupabaseProvider";
import { useGameState } from "../providers/GameStateProvider";
import GameCanvas from "../components/GameCanvas";
import CongratsModal from "../components/CongratsModal";
import TutorialOverlay from "../components/TutorialOverlay";
import Page from "../components/Page";
import { Level } from "../data/LevelModel";
import { backendService } from "../services/backendService";
import LevelInfoBar from "../components/LevelInfoBar";

const Budgeting: React.FC = () => {
  const { user, updateTokens } = useSupabase();
  const {
    currentLevel,
    setCurrentLevel,
    setAdultMode,
    statsLevel,
    setStatsLevel,
    markLevelCompleted,
  } = useGameState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isInitializedRef = useRef(false);

  // Tutorial state
  const [showTutorial, setShowTutorial] = useState(false);

  // Congrats modal state
  const [showCongrats, setShowCongrats] = useState(false);
  const [winData, setWinData] = useState({
    score: 0,
    tokensAwarded: 0,
    moves: 0,
    duration: 0,
    mistakes: [] as string[],
  });

  // Check if this is the user's first time
  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem("hasSeenBudgetTutorial");
    if (!hasSeenTutorial && currentLevel && !loading) {
      setShowTutorial(true);
    }
  }, [currentLevel, loading]);

  const handleTutorialComplete = () => {
    localStorage.setItem("hasSeenBudgetTutorial", "true");
    setShowTutorial(false);
  };

  const handleTutorialSkip = () => {
    localStorage.setItem("hasSeenBudgetTutorial", "true");
    setShowTutorial(false);
  };

  useEffect(() => {
    // Only run once when we have a user and haven't initialized yet
    if (!user || isInitializedRef.current) {
      if (!user) {
        setLoading(false);
      }
      return;
    }

    // Set initialized flag immediately to prevent race conditions
    isInitializedRef.current = true;

    const fetchLevel = async () => {
      try {
        setLoading(true);
        setError(null);

        // Set adultMode from user preferences
        setAdultMode(user.adultMode);

        // Fetch level from backend
        const level = await backendService.fetchNextLevel(
          user.id,
          currentLevel?.input_level, // Pass current level ID if available
        );

        setCurrentLevel(level);

        // Fetch latest stats (tokens & user level)
        try {
          const stats = await backendService.fetchLevelStats(user.id);
          if (stats) {
            if (typeof stats.tokens === "number") {
              updateTokens(stats.tokens);
            }
            if (typeof stats.level === "number") {
              setStatsLevel(stats.level);
            }
          }
        } catch (statsErr) {
          console.warn("Failed to fetch level stats:", statsErr);
        }

        console.log("Successfully loaded level from backend:", level);
      } catch (err) {
        console.error("Failed to fetch level from backend:", err);
        setError("Failed to load level. Please try again.");

        // Fallback to a basic level if backend fails
        const fallbackLevel = {
          id: Date.now(),
          level: 1,
          input_level: 1,
          nickname: "Offline Level",
          active: true,
          reviewed: false,
          categories: [
            {
              group: "Food",
              name: "Food",
              necessary: true,
              budgeted: 100,
              spent: 150,
              savings: false,
            },
            {
              group: "Entertainment",
              name: "Entertainment",
              necessary: false,
              budgeted: 50,
              spent: 75,
              savings: false,
            },
          ],
          tokens: 25,
          weighted_dials: 0.5,
          dial_precision: 25,
          dial_visible_categories: 2,
          dial_unbalanced_categories: 2,
          dial_surprise_events: 0,
          dial_steps: 3,
          dial_budget: 150,
          dial_income: 0,
          dial_savings: 0,
          dial_surprise_amount: 0,
        };

        setCurrentLevel(new Level(fallbackLevel));
      } finally {
        setLoading(false);
      }
    };

    fetchLevel();
  }, [user]); // Only depend on user, ref doesn't need to be in deps

  // Listen for win events from the game
  useEffect(() => {
    const handleLevelComplete = (event: CustomEvent) => {
      setWinData({
        score: event.detail.score,
        tokensAwarded: event.detail.tokens,
        moves: event.detail.moves,
        duration: event.detail.duration,
        mistakes: event.detail.mistakes,
      });
      setShowCongrats(true);

      // Update completed levels if this is a YIC level
      if (currentLevel?.nickname?.startsWith("yic-")) {
        markLevelCompleted(currentLevel.nickname);
      }

      // Tokens awarded shown in modal; defer global update until modal is dismissed.
    };

    // Add event listener for level completion
    window.addEventListener(
      "levelComplete",
      handleLevelComplete as EventListener,
    );

    return () => {
      window.removeEventListener(
        "levelComplete",
        handleLevelComplete as EventListener,
      );
    };
  }, [currentLevel, markLevelCompleted]);

  // Refresh stats when the Congrats modal is dismissed
  useEffect(() => {
    const handleModalClosed = () => {
      if (!user) return;
      backendService
        .fetchLevelStats(user.id)
        .then((stats) => {
          if (stats) {
            if (typeof stats.tokens === "number") {
              updateTokens(stats.tokens);
            }
            if (typeof stats.level === "number") {
              setStatsLevel(stats.level);
            }
          }
        })
        .catch((err) =>
          console.warn("Failed to refresh stats after modal close", err),
        );
    };

    window.addEventListener("modalClosed", handleModalClosed);
    return () => {
      window.removeEventListener("modalClosed", handleModalClosed);
    };
  }, [user, updateTokens, setStatsLevel]);

  // Update stats level immediately when level completes (before modal)
  useEffect(() => {
    const handleLevelComplete = () => {
      if (!user) return;
      // Immediately update stats level to trigger hint visibility recalculation
      backendService
        .fetchLevelStats(user.id)
        .then((stats) => {
          if (stats && typeof stats.level === "number") {
            setStatsLevel(stats.level);
          }
        })
        .catch((err) =>
          console.warn("Failed to refresh stats after level completion", err),
        );
    };

    window.addEventListener("levelComplete", handleLevelComplete);
    return () => {
      window.removeEventListener("levelComplete", handleLevelComplete);
    };
  }, [user, setStatsLevel]);

  // Show loading state
  if (loading) {
    return (
      <Page title="Budget Game" hideHeader>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "#000000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CircularProgress sx={{ color: "#ffffff" }} />
          <Box sx={{ color: "#ffffff", fontSize: "18px" }}>
            Loading level...
          </Box>
        </Box>
      </Page>
    );
  }

  return (
    <Page title="Budget Game" hideHeader>
      {error && (
        <Alert
          severity="warning"
          sx={{
            position: "absolute",
            top: 10,
            left: 10,
            right: 10,
            zIndex: 1000,
          }}
        >
          {error}
        </Alert>
      )}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: "#000000",
        }}
      >
        {/* Level metadata bar */}
        <LevelInfoBar />

        <GameCanvas
          levelId={currentLevel ? String(currentLevel.id) : undefined}
        />
      </Box>

      {/* Congratulations Modal */}
      <CongratsModal
        open={showCongrats}
        onClose={() => setShowCongrats(false)}
        score={winData.score}
        tokensAwarded={winData.tokensAwarded}
        moves={winData.moves}
        duration={winData.duration}
        mistakes={winData.mistakes}
      />

      {/* Tutorial Overlay */}
      <TutorialOverlay
        open={showTutorial}
        onComplete={handleTutorialComplete}
        onSkip={handleTutorialSkip}
      />
    </Page>
  );
};

export default Budgeting;
